import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { useThemePalette } from '@/config/theme';

interface DiscoverEntry {
  key: string;
  icon: keyof typeof Ionicons.glyphMap;
  tint: string;
  title: string;
  subtitle: string;
  path: string;
}

/** One Discover tile: a glassy card with a tinted icon puck, title + one-liner. */
function DiscoverTile({ entry }: { entry: DiscoverEntry }) {
  const router = useRouter();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={entry.title}
      onPress={() => router.push(entry.path as never)}
      style={{ width: 164 }}
      className="rounded-card border border-glass-border bg-glass p-3 active:opacity-80"
    >
      <View
        className="mb-2.5 h-9 w-9 items-center justify-center rounded-full"
        style={{ backgroundColor: `${entry.tint}22` }}
      >
        <Ionicons name={entry.icon} size={18} color={entry.tint} />
      </View>
      <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
        {entry.title}
      </AppText>
      <AppText variant="micro" className="mt-0.5 leading-4 text-muted" numberOfLines={2}>
        {entry.subtitle}
      </AppText>
    </Pressable>
  );
}

/**
 * The home "Discover" rail: a horizontally-scrolling strip of launch tiles that
 * surfaces the secondary content areas (World Cup news, the group tables &
 * bracket, the esports/sports scores, and tournament history) directly on the
 * Matches home so they're no longer buried inside the Today hub.
 *
 * Rendered inside the Matches FlatList's ListHeaderComponent, so it never breaks
 * list virtualization. RTL-safe (the ScrollView + flex flip with the writing
 * system) and themed entirely via palette tokens.
 */
export function DiscoverRail() {
  const { t } = useTranslation();
  const palette = useThemePalette();

  const entries: DiscoverEntry[] = [
    {
      key: 'news',
      icon: 'newspaper',
      tint: palette.voltBright,
      title: t('today.newsTitle'),
      subtitle: t('discover.newsSub'),
      path: '/news',
    },
    {
      key: 'groups',
      icon: 'trophy',
      tint: palette.captain,
      title: t('today.wcGroupsTitle'),
      subtitle: t('discover.groupsSub'),
      path: '/wc-groups',
    },
    {
      key: 'sports',
      icon: 'game-controller',
      tint: palette.volt,
      title: t('today.sportsTitle'),
      subtitle: t('discover.sportsSub'),
      path: '/sports',
    },
    {
      key: 'history',
      icon: 'medal',
      tint: palette.win,
      title: t('history.title'),
      subtitle: t('discover.historySub'),
      path: '/history',
    },
  ];

  return (
    <View className="pb-5">
      <View className="mx-4 mb-2.5 flex-row items-center gap-2">
        <Ionicons name="compass-outline" size={15} color={palette.soft} />
        <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
          {t('discover.title')}
        </AppText>
        <View className="h-px flex-1 bg-line" />
      </View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 16, gap: 10 }}
      >
        {entries.map((entry) => (
          <DiscoverTile key={entry.key} entry={entry} />
        ))}
      </ScrollView>
    </View>
  );
}
